import { createFileRoute, Link, Navigate, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { workspaceSlug } from "../lib/hosts";
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Layout, Plus, SlidersHorizontal, Users } from "lucide-react";
import { peopleViewer, seesPeople as seesPeopleOf, useMembers, useWorkspace } from "../lib/workspaces";
import { contributes } from "../../shared/schemas/workspaces";
import type { LaneKind } from "../../shared/schemas/boards";
import { useMe, meUser } from "../lib/auth";
import { useProject, useProjectByKey } from "../lib/projects";
import { useLabels } from "../lib/labels";
import { useAssignToMe, useBoard, useMoveIssue, prefetchIssue } from "../lib/issues";
import { issueSearchSchema, searchToFilters, hasActiveFilters, withoutFilters } from "../lib/issue-filters";
import { IssueFilters } from "../components/issues/IssueFilters";
import { LaneChoice, QuickFilters } from "../components/issues/QuickFilters";
import { Board } from "../components/issues/Board";
import { CreateIssueDialog } from "../components/issues/CreateIssueDialog";
import { IssuePeek } from "../components/issues/IssuePeek";
import { useDeleteWithUndo } from "../components/issues/use-delete-with-undo";
import { useIssueShortcuts, type QuickControl } from "../components/issues/use-issue-shortcuts";
import { ProjectHeading } from "../components/projects/ProjectHeading";
import { ProjectKeyMoved, ProjectUnreachable } from "../components/projects/ProjectStates";
import { NoSprintRunning, SprintBoardHeader } from "../components/backlog/SprintBoardHeader";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Unavailable } from "../components/ui/states";
import { strings } from "../lib/strings";
import { fill } from "../lib/words";
import { ApiError } from "../lib/api";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";

const words = strings.issues.board;
/** The supported envelope of unresolved issues on one board (BUILD_SPEC §22.3). */
const ENVELOPE = 5_000;

/** The lanes this person keeps folded on this board, remembered in this browser: a convenience, forgotten if refused. */
function rememberedLanes(key: string): Set<string> {
  try {
    const held = window.localStorage.getItem(key);
    return new Set(held === null ? [] : (JSON.parse(held) as string[]));
  } catch {
    return new Set();
  }
}

function BoardPage() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/" });
  const slug = workspaceSlug();
  const search = useSearch({ from: "/admin/p/$projectKey/" });
  const navigate = useNavigate({ from: "/admin/p/$projectKey/" });
  const view = useWorkspace(slug);
  const byKey = useProjectByKey(slug, projectKey);
  const me = useMe();
  const members = useMembers(slug);
  const labels = useLabels();
  const [createOpen, setCreateOpen] = useState(false);
  const [focusedIssueId, setFocusedIssueId] = useState<string | null>(null);
  const [quickControl, setQuickControl] = useState<QuickControl | null>(null);
  const [whole, setWhole] = useState<ReadonlySet<string>>(new Set());
  const searchRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const project = byKey.project;
  const detail = useProject(project?.id ?? "");
  const statuses = detail.data?.statuses ?? [];
  // A Viewer reads the board and changes nothing (BUILD_SPEC §8.2); the API refuses them as well. Nor does anyone,
  // until the project says they may: an archived project, or one not yet loaded, offers no change.
  const contributor = view.data !== undefined && contributes(view.data.role) && detail.data?.canEdit === true;
  const seesPeople = seesPeopleOf(peopleViewer(view.data, meUser(me)?.id));
  const filters = searchToFilters(search);
  const board = useBoard(project?.id ?? "", filters, search.lane, whole);
  const issues = (board.data?.columns ?? []).flatMap((column) => column.issues);
  const move = useMoveIssue(project?.id ?? "");
  const assign = useAssignToMe();
  const lane: LaneKind = board.data?.lane ?? search.lane ?? "none";
  const lanesKey = `steer.board.lanes.${project?.id ?? ""}.${lane}`;
  const [folded, setFolded] = useState<{ key: string; lanes: ReadonlySet<string> }>({ key: "", lanes: new Set() });
  if (folded.key !== lanesKey) setFolded({ key: lanesKey, lanes: rememberedLanes(lanesKey) });

  const prefetch = (id: string) => {
    prefetchIssue(qc, id);
    void import("../components/issues/MarkdownView").catch(() => undefined);
  };

  // Resolve the open-drawer issue (key in URL → id from the warm list cache).
  const drawerIssue = search.issue ? issues.find((i) => i.key === search.issue) : undefined;
  const deleteWithUndo = useDeleteWithUndo(project?.id ?? "");

  useIssueShortcuts({
    issues,
    peeking: drawerIssue !== undefined,
    onExpand: () =>
      drawerIssue !== undefined &&
      void navigate({ to: "/admin/p/$projectKey/$issueKey", params: { projectKey, issueKey: drawerIssue.key } }),
    focusedIssueId,
    setFocusedIssueId,
    onOpen: (issue) => void navigate({ search: (prev) => ({ ...prev, issue: issue.key }) }),
    onCreate: () => {
      if (contributor) setCreateOpen(true);
    },
    onFocusSearch: () => searchRef.current?.focus(),
    onAssignMe: contributor ? assign.one : undefined,
    onQuick: (issue, control) => {
      setQuickControl(control);
      void navigate({ search: (prev) => ({ ...prev, issue: issue.key }) });
    },
  });

  if (view.isLoading || byKey.isLoading || (project !== undefined && detail.isLoading)) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((c) => (
            <Skeleton key={c} className="h-64 w-full" />
          ))}
        </div>
      </div>
    );
  }
  if ((view.error instanceof ApiError && (view.error.status === 404 || view.error.status === 401)) || !view.data) {
    return <ToWorkspaces />;
  }
  if (!byKey.loaded)
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Unavailable what={strings.projects.list.theProjects} onRetry={byKey.refetch} page />
      </div>
    );
  if (!project || project.joinable) return <ProjectUnreachable project={project} />;
  if (project.key !== projectKey) return <ProjectKeyMoved from={projectKey} to={project.key} />;
  // A Plan project leads with its timeline, not a board (BUILD_SPEC §14.3).
  if (project.method === "plan") return <Navigate to="/admin/p/$projectKey/timeline" params={{ projectKey }} replace />;

  const memberName = (id: string | null) =>
    id ? ((members.data ?? []).find((m) => m.userId === id)?.userName ?? null) : null;
  const openIssue = (issueKey: string) => navigate({ search: (prev) => ({ ...prev, issue: issueKey }) });
  const closeDrawer = () => {
    setQuickControl(null);
    void navigate({ search: (prev) => ({ ...prev, issue: undefined }) });
  };
  const toggleLane = (key: string) => {
    const next = new Set(folded.lanes);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setFolded({ key: lanesKey, lanes: next });
    try {
      window.localStorage.setItem(lanesKey, JSON.stringify([...next]));
    } catch {
      // Remembering is a convenience; the lanes fold all the same.
    }
  };

  // Empty only once the board has answered: one that couldn't be loaded says so, rather than that there's no work.
  const isEmpty = board.data !== undefined && board.data.columns.every((column) => column.total === 0);
  // A Scrum board is its running sprint (BUILD_SPEC §12.2).
  const scrum = (detail.data?.project ?? project).method === "scrum";
  const canManage = detail.data?.canManage ?? false;
  const loadingRest = board.isFetching ? whole : new Set<string>();

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <ProjectHeading
            project={detail.data?.project ?? project}
            projectKey={projectKey}
            current="board"
            canManage={canManage}
          />
          <div className="flex items-center gap-2">
            {canManage && (
              <Button size="sm" variant="ghost" asChild>
                <Link to="/admin/p/$projectKey/settings" params={{ projectKey }} hash="board">
                  <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden />
                  {words.settings}
                </Link>
              </Button>
            )}
            {contributor && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {words.newIssue}
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <QuickFilters
            value={search}
            projectId={project.id}
            onChange={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }) })}
          />
          <LaneChoice
            value={lane}
            onChange={(next) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  lane: next === board.data?.lane && search.lane === undefined ? undefined : next,
                }),
              })
            }
          />
        </div>
        <IssueFilters
          slug={slug}
          statuses={statuses}
          types={detail.data?.types ?? []}
          showStatus={false}
          value={search}
          onChange={(next) => void navigate({ search: (prev) => ({ ...prev, ...next }) })}
          searchInputRef={searchRef}
        />
      </header>

      {board.data !== undefined && board.data.unresolved > ENVELOPE && (
        <p
          role="status"
          className="mb-4 inline-flex items-center gap-2 rounded-md bg-[hsl(var(--muted))] px-3 py-2 text-sm"
        >
          <AlertTriangle className="h-4 w-4" aria-hidden />
          {fill(words.envelope, { count: board.data.unresolved })}
        </p>
      )}
      {scrum && board.data !== undefined && board.data.sprint !== null && (
        <SprintBoardHeader
          sprint={board.data.sprint}
          issues={issues}
          projectId={project.id}
          canManage={canManage && project.archivedAt === null}
        />
      )}
      {scrum && board.data !== undefined && board.data.sprint === null ? (
        <NoSprintRunning projectKey={projectKey} />
      ) : isEmpty && hasActiveFilters(search) ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
          <h2 className="text-lg font-semibold">{strings.issues.filters.emptyTitle}</h2>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{strings.issues.filters.emptyBody}</p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => void navigate({ search: (prev) => withoutFilters(prev) })}
          >
            {strings.issues.filters.clear}
          </Button>
        </div>
      ) : isEmpty ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--muted))]">
            <Layout className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
          </div>
          <h2 className="mt-4 text-lg font-semibold">{words.emptyTitle}</h2>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{words.emptyBody}</p>
          <div className="mt-6 flex items-center justify-center gap-2">
            {contributor && <Button onClick={() => setCreateOpen(true)}>{words.emptyCreate}</Button>}
            {seesPeople && (
              <Button variant="outline" asChild>
                <a href="/admin/settings/people">
                  <Users className="mr-2 h-4 w-4" />
                  {words.emptyInvite}
                </a>
              </Button>
            )}
          </div>
        </div>
      ) : board.data === undefined && board.isError ? (
        <Unavailable what={words.theBoard} onRetry={() => void board.refetch()} />
      ) : board.data === undefined ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          {[0, 1, 2, 3].map((c) => (
            <Skeleton key={c} className="h-64 w-full" />
          ))}
        </div>
      ) : (
        <Board
          answer={board.data}
          statuses={statuses}
          types={detail.data?.types ?? []}
          labels={new Map((labels.data ?? []).map((label) => [label.id, label]))}
          memberName={memberName}
          focusedIssueId={focusedIssueId}
          collapsedLanes={folded.lanes}
          onToggleLane={toggleLane}
          onOpenIssue={(issue) => void openIssue(issue.key)}
          onMove={
            contributor
              ? ({ issue, statusId, after, before, rank }) =>
                  move.mutate({
                    issueId: issue.id,
                    move: {
                      ...(statusId === undefined ? {} : { statusId }),
                      ...(after === undefined ? {} : { after }),
                      ...(before === undefined ? {} : { before }),
                    },
                    ...(rank === undefined ? {} : { rank }),
                  })
              : undefined
          }
          onShowRest={(columnId) => setWhole(new Set([...whole, columnId]))}
          loadingRest={loadingRest}
          onPrefetch={prefetch}
        />
      )}

      <CreateIssueDialog open={createOpen} onOpenChange={setCreateOpen} projectId={project.id} slug={slug} />

      {drawerIssue && (
        <IssuePeek
          issueId={drawerIssue.id}
          issueKey={drawerIssue.key}
          slug={slug}
          projectId={project.id}
          pageNeeds={1000}
          onClose={closeDrawer}
          onExpand={() =>
            void navigate({ to: "/admin/p/$projectKey/$issueKey", params: { projectKey, issueKey: drawerIssue.key } })
          }
          onRequestDelete={() => deleteWithUndo.requestDelete(drawerIssue.id, { onStart: closeDrawer })}
          issueKeyForId={(id) => issues.find((i) => i.id === id)?.key ?? null}
          autoOpen={quickControl ?? undefined}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/")({
  validateSearch: issueSearchSchema,
  component: BoardPage,
});
